import { useEffect, useMemo, useState } from 'react';
import {
  FiEdit2,
  FiPlus,
  FiSearch,
  FiTrash2,
  FiUser,
  FiBriefcase,
  FiFilter,
  FiHash,
  FiFolder,
  FiActivity
} from 'react-icons/fi';
import { useNavigate } from 'react-router-dom';
import Api from '../../Api';
import { usePermission } from '../../hooks/usePermission';
import {
  Button,
  Card,
  Input,
  Modal,
  PageComponent,
  Select,
  Skeletons,
  Table,
  AccountantForm,
  MultiSelect,
} from '../../components';
import TableActions from '../../components/Table/TableActions';
import EntityFilesModal from '../../components/EntityFilesModal';
import '../CompanyManager/style.css';

export default function AccountantManager() {
  const permissions = usePermission('management');
  const [accountants, setAccountants] = useState([]);
  const [loading, setLoading] = useState(true);



  const [hasLoadedOnce, setHasLoadedOnce] = useState(false);
  const [error, setError] = useState('');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedAccountant, setSelectedAccountant] = useState(null);
  const [isFilesModalOpen, setIsFilesModalOpen] = useState(false);
  const [selectedAccountantForFiles, setSelectedAccountantForFiles] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [accountantToDelete, setAccountantToDelete] = useState(null);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  
  // Multi-company state
  const [companies, setCompanies] = useState([]);
  const [companyFilter, setCompanyFilter] = useState('all');
  const [company, setCompany] = useState(null);
  const [companyFilterValues, setCompanyFilterValues] = useState(['all']);
  
  const navigate = useNavigate();

  useEffect(() => {
    if (permissions.canView) {
      loadInitialData();
    }
  }, [permissions.canView]);

  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, companyFilter]);

  function handleCompanyFilterChange(newValues) {
    const allValue = 'all';
    const currentSelected = companyFilterValues;
    const isAllSelected = newValues.includes(allValue);
    const wasAllSelected = currentSelected.includes(allValue);

    let nextValues;

    if (isAllSelected && !wasAllSelected) {
      const allIds = companies.map(c => String(c.id));
      nextValues = [allValue, ...allIds];
    } else if (!isAllSelected && wasAllSelected) {
      nextValues = [];
    } else if (wasAllSelected && newValues.length < currentSelected.length) {
      const remaining = newValues.filter(v => v !== allValue);
      nextValues = remaining;
    } else {
      const specificIds = newValues.filter(v => v !== allValue);
      if (specificIds.length === companies.length && companies.length > 0) {
        nextValues = [allValue, ...specificIds];
      } else {
        nextValues = specificIds;
      }
    }

    setCompanyFilterValues(nextValues);

    const idsWithoutAll = nextValues.filter(v => v !== allValue);
    if (idsWithoutAll.length === 1) {
      const singleId = idsWithoutAll[0];
      setCompanyFilter(singleId);
      loadAccountants(singleId);
    } else {
      setCompanyFilter('all');
      loadAccountants('all');
    }
  }

  async function loadInitialData() {
    try {
      setLoading(true);
      const companiesData = await Api.getCompanies();
      if (Array.isArray(companiesData)) {
        setCompanies(companiesData);
        // Default to 'all' per user preference
        setCompanyFilter('all');
        await loadAccountants('all', companiesData);
      }
    } catch (err) {
      console.error('Erro ao carregar empresas:', err);
      setError('Erro ao carregar dados iniciais.');
    } finally {
      setLoading(false);
    }
  }

  async function loadAccountants(filterValue = companyFilter, companiesList = companies) {
    try {
      setLoading(true);
      let data = [];

      if (filterValue === 'all') {
        // Fetch for all companies
        const promises = companiesList.map(comp => 
          Api.getAccountants(comp.id).then(res => 
            Array.isArray(res) ? res.map(a => ({ ...a, company: comp, company_id: comp.id })) : []
          ).catch(() => [])
        );
        const results = await Promise.all(promises);
        data = results.flat();
      } else {
        // Fetch for specific company
        const res = await Api.getAccountants(filterValue);
        if (Array.isArray(res)) {
          const selectedComp = companiesList.find(c => String(c.id) === String(filterValue));
          data = res.map(a => ({ ...a, company: selectedComp, company_id: filterValue }));
          setCompany(selectedComp);
        }
      }

      if (Array.isArray(data)) {
        setAccountants(data);
        setHasLoadedOnce(true);
      } else {
        setError('Não foi possível carregar os contadores.');
      }
    } catch (err) {
      setError('Erro ao carregar contadores. Tente novamente.');
    } finally {
      setLoading(false);
    }
  }

  function handleCreate() {
    setSelectedAccountant(null);
    setIsModalOpen(true);
  }

  function handleEdit(accountant) {
    setSelectedAccountant(accountant);
    setIsModalOpen(true);
  }

  function handleFiles(accountant) {
    setSelectedAccountantForFiles(accountant);
    setIsFilesModalOpen(true);
  }

  function handleFormSuccess() {
    loadAccountants(companyFilter);
    setIsModalOpen(false);
  }

  function handleDeleteClick(accountant) {
    setAccountantToDelete(accountant);
    setIsDeleteModalOpen(true);
  }

  async function handleConfirmDelete() {
    if (!accountantToDelete) return;

    // Use accountant's company_id or fallback to current context
    const targetCompanyId = accountantToDelete.company_id || accountantToDelete.company?.id || company?.id;
    if (!targetCompanyId) {
        alert('Erro: Empresa não identificada para exclusão.');
        return;
    }

    try {
      setIsDeleting(true);
      await Api.deleteAccountant(targetCompanyId, accountantToDelete.id);
      loadAccountants(companyFilter);
      setIsDeleteModalOpen(false);
      setAccountantToDelete(null);
    } catch (err) {
      console.error(err);
      alert('Erro ao excluir contador.');
    } finally {
      setIsDeleting(false);
    }
  }

  const filteredAccountants = useMemo(() => {
    const allValue = 'all';
    const specificIds = companyFilterValues.filter(v => v !== allValue);
    const hasAllSelected = companyFilterValues.includes(allValue);

    return accountants.filter(accountant => {
      const searchLower = searchTerm.toLowerCase();
      const matchesSearch =
        accountant.name?.toLowerCase().includes(searchLower) ||
        accountant.trade_name?.toLowerCase().includes(searchLower) ||
        accountant.document?.includes(searchLower) ||
        accountant.email?.toLowerCase().includes(searchLower);

      const companyId = accountant.company_id || accountant.company?.id;
      const matchesCompany =
        hasAllSelected ||
        specificIds.length === 0 ||
        specificIds.includes(String(companyId));

      return matchesSearch && matchesCompany;
    });
  }, [accountants, searchTerm, companyFilterValues]);

  const totalItems = filteredAccountants.length;
  const totalPages = totalItems > 0 ? Math.ceil(totalItems / pageSize) : 1;
  const safePage = Math.min(currentPage, totalPages);
  const startIndex = (safePage - 1) * pageSize;
  const paginatedAccountants = filteredAccountants.slice(
    startIndex,
    startIndex + pageSize,
  );

  const columns = [
    {
      id: 'name',
      header: 'Contador',
      width: '2.5fr',
      cellClassName: 'manager-table-cell-primary',
      render: accountant => (
        <>
          <div className="manager-table-avatar">
            {accountant.type === 'PJ' ? <FiBriefcase size={18} /> : <FiUser size={18} />}
          </div>
          <div className="manager-table-cell-text">
            <span className="manager-table-title">
              {accountant.name}
            </span>
            <span className="manager-table-subtitle">
              {accountant.trade_name || 'Sem nome fantasia'}
            </span>
          </div>
        </>
      ),
    },
    {
      id: 'document',
      header: 'Documento',
      width: '1.8fr',
      cellClassName: 'manager-table-cell manager-table-col-cnpj',
      render: accountant => (
        <div className="manager-table-cnpj">
          <FiHash size={14} />
          <span>{accountant.document || 'Não informado'}</span>
        </div>
      ),
    },
    {
      id: 'contact',
      header: 'Contato',
      width: '2fr',
      cellClassName: 'manager-table-cell',
      render: accountant => {
        const email = accountant.email || 'E-mail não informado';
        const phone = accountant.mobile || accountant.phone || 'Telefone não informado';

        return (
          <div className="manager-table-cell-text">
            <span className="manager-table-subtitle">
              {email}
            </span>
            <span className="manager-table-subtitle">
              {phone}
            </span>
          </div>
        );
      },
    },
    {
      id: 'status',
      header: 'Status',
      width: '1.4fr',
      cellClassName: 'manager-table-cell manager-table-col-tags',
      render: accountant => {
        const isActive = accountant.active !== false;

        return (
          <div className="manager-tags">
            <span
              className={[
                'manager-tag',
                isActive ? 'manager-tag-success' : 'manager-tag-danger',
              ]
                .filter(Boolean)
                .join(' ')}
            >
              {isActive ? 'Ativo' : 'Inativo'}
            </span>
          </div>
        );
      },
    },
    {
      id: 'company',
      header: 'Empresa',
      width: '1.5fr',
      cellClassName: 'manager-table-cell',
      render: row => {
        const companyName = row.company ? (row.company.trade_name || row.company.name) : 'N/A';
        return <span className="manager-table-text" style={{ fontWeight: 'normal' }}>{companyName}</span>;
      },
    },
    {
      id: 'actions',
      header: 'Ações',
      width: '0.6fr',
      cellClassName: 'manager-table-cell manager-table-col-actions',
      render: accountant => (
        <TableActions>
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              handleEdit(accountant);
            }}
          >
            <FiEdit2 size={16} />
            Editar
          </button>
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              handleFiles(accountant);
            }}
          >
            <FiFolder size={16} />
            Arquivos
          </button>
          <button
            type="button"
            className="action-danger"
            onClick={(e) => {
              e.stopPropagation();
              handleDeleteClick(accountant);
            }}
          >
            <FiTrash2 size={16} />
            Excluir
          </button>
        </TableActions>
      ),
    },
  ];

  function AccountantManagerSkeletonWrapper() {
    const { CompanyManagerSkeleton } = Skeletons;
    return <CompanyManagerSkeleton />;
  }

  const companyOptions = useMemo(() => {
    const opts = companies.map(c => ({
      value: c.id,
      label: c.trade_name || c.name
    }));
    return [{ value: 'all', label: 'TODAS' }, ...opts];
  }, [companies]);

  if (!permissions.canView) {
    return (
      <PageComponent authenticatedPage={true}>
        <div className="container">
          <div className="manager-header">
            <div className="manager-header-title">
              <div className="manager-header-icon">
                <FiActivity size={22} />
              </div>
              <div>
                <h1>Acesso Negado</h1>
                <p>Você não tem permissão para visualizar esta página.</p>
              </div>
            </div>
          </div>
        </div>
      </PageComponent>
    );
  }

  return (
    <PageComponent authenticatedPage={true}>
      <div className="container">
        {loading && !hasLoadedOnce && <AccountantManagerSkeletonWrapper />}

        {(hasLoadedOnce || !loading) && (
          <>
            <div className="manager-header">
              <div className="manager-header-title">
                <div className="manager-header-icon">
                  <FiUser size={22} />
                </div>
                <div>
                  <h1>Contadores</h1>
                  <p>
                    Visualize, filtre e gerencie todos os contadores da empresa selecionada.
                  </p>
                </div>
              </div>
              {permissions.canCreate && (
                <Button onClick={handleCreate} className="manager-header-button">
                  <FiPlus size={18} />
                  Novo Contador
                </Button>
              )}
            </div>

            <Card className="manager-filters-card">
              <div className="manager-filters-header">
                <div className="manager-filters-title">
                  <FiFilter size={16} />
                  <span>Filtros</span>
                </div>
                {accountants.length > 0 && (
                  <span className="manager-filters-count">
                    {filteredAccountants.length} de {accountants.length} contadores
                  </span>
                )}
              </div>

              <div className="manager-filters-grid-custom">
                <div className="manager-filter-group">
                  <label className="manager-filter-label">Empresa</label>
                  <MultiSelect
                    id="company-filter"
                    options={companyOptions}
                    value={companyFilterValues}
                    onChange={handleCompanyFilterChange}
                    searchable
                  />
                </div>
                <div className="manager-filter-group">
                  <label className="manager-filter-label">Buscar</label>
                  <div className="manager-filter-search">
                    <Input
                      id="accountant-search"
                      placeholder="Busque por nome, documento ou e-mail"
                      value={searchTerm}
                      onChange={event =>
                        setSearchTerm(event.target.value)
                      }
                      icon={FiSearch}
                    />
                  </div>
                </div>
              </div>
            </Card>

            {error && <div className="error">{error}</div>}

            {!error && accountants.length === 0 && (
              <div className="empty-state">
                <p>Nenhum contador cadastrado.</p>
              </div>
            )}

            {!error && accountants.length > 0 && (
              <Table
                items={paginatedAccountants}
                columns={columns}
                totalItems={totalItems}
                page={safePage}
                totalPages={totalPages}
                pageSize={pageSize}
                onPageChange={setCurrentPage}
                onPageSizeChange={value => {
                  setPageSize(value);
                  setCurrentPage(1);
                }}
                getRowKey={accountant => accountant.id}
                emptyMessage="Nenhum contador encontrado."
              />
            )}
          </>
        )}
      </div>

      <EntityFilesModal
        isOpen={isFilesModalOpen}
        onClose={() => setIsFilesModalOpen(false)}
        entity={selectedAccountantForFiles}
        entityType="accountant"
        company={selectedAccountantForFiles?.company || company}
      />

      <Modal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        title={selectedAccountant ? 'Editar Contador' : 'Novo Contador'}
        size='xl'
      >
        <AccountantForm
          companyId={selectedAccountant?.company_id || (companyFilter === 'all' ? null : companyFilter)}
          companies={companies}
          initialData={selectedAccountant}
          onSuccess={handleFormSuccess}
          onCancel={() => setIsModalOpen(false)}
        />
      </Modal>

      <Modal
        isOpen={isDeleteModalOpen}
        onClose={() => setIsDeleteModalOpen(false)}
        title="Excluir Contador"
      >
        <p>Tem certeza que deseja excluir este contador?</p>
        <div className="manager-delete-actions">
          <Button
            variant="outline"
            onClick={() => setIsDeleteModalOpen(false)}
            disabled={isDeleting}
          >
            Cancelar
          </Button>
          <Button
            variant="danger"
            onClick={handleConfirmDelete}
            disabled={isDeleting}
          >
            {isDeleting ? 'Excluindo...' : 'Excluir'}
          </Button>
        </div>
      </Modal>
    </PageComponent>
  );
}
